/**
 * Customer Action Modals
 * Provides modal dialogs for customer quick actions
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";

// Types
interface CustomerActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  customerId: string;
}

interface CreatePromiseModalProps extends CustomerActionModalProps {
  outstandingAmount?: number;
  currency?: string;
  onSave?: (data: PromiseFormData) => void;
}

interface AddNoteModalProps extends CustomerActionModalProps {
  onSave?: (data: NoteFormData) => void;
}

interface CreateDisputeModalProps extends CustomerActionModalProps {
  outstandingAmount?: number;
  currency?: string;
  onSave?: (data: DisputeFormData) => void;
}

export interface PromiseFormData {
  promiseDate: Date;
  amount: number;
  method: string;
  notes: string;
  sendConfirmation: boolean;
}

export interface NoteFormData {
  subject: string;
  content: string;
  category: string;
  isImportant: boolean;
}

export interface DisputeFormData {
  invoiceNumber: string;
  disputeAmount: number;
  reason: string;
  description: string;
  priority: string;
}

/**
 * Create Promise to Pay Modal
 */
export function CreatePromiseModal({
  open,
  onOpenChange,
  customerName,
  customerId,
  outstandingAmount = 0,
  currency = "GBP",
  onSave,
}: CreatePromiseModalProps) {
  const [promiseDate, setPromiseDate] = useState<Date>();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [sendConfirmation, setSendConfirmation] = useState(true);

  const handleSave = () => {
    // Validation
    if (!promiseDate) {
      toast.error("Please select a promise date");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!method) {
      toast.error("Please select a payment method");
      return;
    }

    const formData: PromiseFormData = {
      promiseDate,
      amount: parseFloat(amount),
      method,
      notes,
      sendConfirmation,
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success("Promise to pay recorded successfully");
    }

    // Reset form
    setPromiseDate(undefined);
    setAmount("");
    setMethod("");
    setNotes("");
    setSendConfirmation(true);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Promise to Pay</DialogTitle>
          <DialogDescription>
            Record a payment promise from {customerName}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input id="customer" value={customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="outstanding">Outstanding Balance</Label>
            <Input
              id="outstanding"
              value={formatCurrency(outstandingAmount, currency)}
              disabled
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="promise-date">Promise Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="promise-date"
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal",
                    !promiseDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {promiseDate ? format(promiseDate, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={promiseDate}
                  onSelect={setPromiseDate}
                  initialFocus
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="amount">Promise Amount *</Label>
            <Input
              id="amount"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="method">Payment Method *</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="method">
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="credit_card">Credit Card</SelectItem>
                <SelectItem value="direct_debit">Direct Debit</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-confirmation"
              checked={sendConfirmation}
              onCheckedChange={(checked) => setSendConfirmation(checked as boolean)}
            />
            <Label
              htmlFor="send-confirmation"
              className="text-sm font-normal cursor-pointer"
            >
              Send confirmation email to customer
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Promise</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add Note Modal
 */
export function AddNoteModal({
  open,
  onOpenChange,
  customerName,
  customerId,
  onSave,
}: AddNoteModalProps) {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [isImportant, setIsImportant] = useState(false);

  const handleSave = () => {
    // Validation
    if (!subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    if (!content.trim()) {
      toast.error("Please enter note content");
      return;
    }
    if (!category) {
      toast.error("Please select a category");
      return;
    }

    const formData: NoteFormData = {
      subject,
      content,
      category,
      isImportant,
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success("Note added successfully");
    }

    // Reset form
    setSubject("");
    setContent("");
    setCategory("");
    setIsImportant(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>Add a note for {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input id="customer" value={customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              placeholder="Enter note subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="category">Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="payment">Payment Discussion</SelectItem>
                <SelectItem value="dispute">Dispute</SelectItem>
                <SelectItem value="credit">Credit Review</SelectItem>
                <SelectItem value="collection">Collection Activity</SelectItem>
                <SelectItem value="account_review">Account Review</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="content">Note Content *</Label>
            <Textarea
              id="content"
              placeholder="Enter note content..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-important"
              checked={isImportant}
              onCheckedChange={(checked) => setIsImportant(checked as boolean)}
            />
            <Label htmlFor="is-important" className="text-sm font-normal cursor-pointer">
              Mark as important
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Create Dispute Modal
 */
export function CreateDisputeModal({
  open,
  onOpenChange,
  customerName,
  customerId,
  outstandingAmount = 0,
  currency = "GBP",
  onSave,
}: CreateDisputeModalProps) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [disputeAmount, setDisputeAmount] = useState("");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");

  const handleSave = () => {
    // Validation
    if (!invoiceNumber.trim()) {
      toast.error("Please enter an invoice number");
      return;
    }
    if (!disputeAmount || parseFloat(disputeAmount) <= 0) {
      toast.error("Please enter a valid dispute amount");
      return;
    }
    if (!reason) {
      toast.error("Please select a dispute reason");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    const formData: DisputeFormData = {
      invoiceNumber,
      disputeAmount: parseFloat(disputeAmount),
      reason,
      description,
      priority,
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success("Dispute created successfully");
    }

    // Reset form
    setInvoiceNumber("");
    setDisputeAmount("");
    setReason("");
    setDescription("");
    setPriority("medium");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Dispute</DialogTitle>
          <DialogDescription>Create a new dispute for {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input id="customer" value={customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="invoice-number">Invoice Number *</Label>
            <Input
              id="invoice-number"
              placeholder="Enter invoice number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="dispute-amount">Dispute Amount *</Label>
            <Input
              id="dispute-amount"
              type="number"
              placeholder="0.00"
              value={disputeAmount}
              onChange={(e) => setDisputeAmount(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reason">Dispute Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="reason">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pricing">Pricing Discrepancy</SelectItem>
                <SelectItem value="quality">Quality Issue</SelectItem>
                <SelectItem value="delivery">Delivery Problem</SelectItem>
                <SelectItem value="not_received">Goods Not Received</SelectItem>
                <SelectItem value="duplicate">Duplicate Invoice</SelectItem>
                <SelectItem value="credit_note">Credit Note Expected</SelectItem>
                <SelectItem value="payment_made">Payment Already Made</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Provide details about the dispute..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Create Dispute</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
